import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SvgIconComponent } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, computed, inject, input, resource, signal } from "@angular/core";

import { FrequencyBand } from "../../../apis/networkdashboard/rest/models";
import type { KpiTypeAerohive, KpiTypeAruba, KpiTypeGeneral } from "../../../apis/networkdashboard/rest/models";
import { StatsService } from "../../../apis/networkdashboard/rest/services";
import { reportAndReturn } from "../../../helpers/report-to-sentry";
import {
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
} from "../../common/line-chart/components/date-picker/date-picker.component";
import { LineChartSubjectDropdown } from "../../common/line-chart/line-chart-subject-dropdown";
import { ChartMetadata, LineChart, LineChartLines } from "../../common/line-chart/line-chart.component";
import { getDateRange } from "../../common/line-chart/line-chart.helpers";
import {
  KpiSubject,
  NUMBER_OF_USERS_KPI_KEY,
  WirelessBand,
  bandLabelKey,
  buildAllBandsLabel,
  buildKpiSubjects,
  wirelessLineColors,
  wirelessStatsToChartData,
} from "./wireless-kpi-chart.helpers";

type KpiSelectItem = { key: string; name: string };

const EMPTY_WIRELESS_STATS = { five: [], six: [], twofour: [] };

@Component({
  selector: "wireless-kpi-chart",
  imports: [LineChart, LineChartSubjectDropdown, SvgIconComponent, TranslatePipe],
  templateUrl: "./wireless-kpi-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./wireless-kpi-chart.component.scss",
})
export class WirelessKpiChart {
  public readonly subscriptionId = input.required<string>();
  public readonly chartChoices = input<KpiSubject[]>([]);
  public readonly supportedBands = input<WirelessBand[]>([]);
  public readonly sensorPresent = input<boolean>(false);

  private readonly statsService = inject(StatsService);
  private readonly translate = inject(TranslateService);

  protected readonly selectedKpiSubject = signal<KpiSelectItem | undefined>(undefined);
  protected readonly selectedKpi = computed(() => this.selectedKpiSubject()?.key ?? NUMBER_OF_USERS_KPI_KEY);
  protected readonly dateRangeOfInterest = signal<[Date, Date]>([DEFAULT_START_DATE, DEFAULT_END_DATE]);

  protected readonly kpiSubjects = computed<KpiSelectItem[]>(() =>
    buildKpiSubjects(this.chartChoices(), this.sensorPresent()).map((s) => ({
      key: s.key,
      name: this.translate.instant(s.label) as string,
    })),
  );

  protected readonly kpis = resource({
    params: () => ({
      subscriptionId: this.subscriptionId(),
      kpiType: this.selectedKpi(),
      dateRange: this.dateRangeOfInterest(),
      supportedBands: this.supportedBands(),
    }),
    loader: async ({ params }) => {
      const { start, end, step } = getDateRange(params.dateRange);
      const raw = await this.statsService
        .getWirelessKpi({
          subscription_id: params.subscriptionId,
          kpi_type: params.kpiType as KpiTypeGeneral | KpiTypeAerohive | KpiTypeAruba,
          frequency: FrequencyBand.All,
          start,
          end,
          step,
        })
        .catch(reportAndReturn(undefined));
      return wirelessStatsToChartData(raw ?? EMPTY_WIRELESS_STATS, params.kpiType);
    },
    defaultValue: {} as LineChartLines,
  });

  protected readonly kpiMetadata = computed<ChartMetadata>(() => {
    const entries = Object.keys(this.kpis.value()).map((key, index) =>
      key === NUMBER_OF_USERS_KPI_KEY ?
        [
          key,
          {
            color: wirelessLineColors[0],
            label: buildAllBandsLabel(this.supportedBands(), (k) => this.translate.instant(k) as string),
          },
        ]
      : [
          key,
          {
            color: wirelessLineColors[index],
            label: this.translate.instant(bandLabelKey(key as WirelessBand)) as string,
          },
        ],
    );
    return Object.fromEntries(entries);
  });
}
